import { apiPost, apiGet, apiPut, apiDelete } from '../../../lib/api';
import { SelectedProduct } from '../types';

export interface ProposalRecord {
  id: string;
  groupId: string;
  version: number;
  isLatest: boolean;
  refNumber: string;
  customerName: string | null;
  projectTitle: string | null;
  totalPrice: number;
  formData: Record<string, unknown> | null;
  breakdownData: Record<string, unknown> | null;
  createdBy: string | null;
  deletedAt: string | null;
  customerPdfUrl: string | null;
  internalPdfUrl: string | null;
  createdAt: string | null;
}

interface SaveProposalPayload {
  groupId?: string;
  refNumber: string;
  customerName?: string;
  projectTitle?: string;
  totalPrice: number;
  formData: object;
  breakdownData?: object;
  customerPdfBase64?: string;
  internalPdfBase64?: string;
}

interface PricingResponse {
  totalPrice?: number;
  total_price?: number;
  finalPrice?: number;
  breakdown?: {
    products?: Record<string, {
      total?: number;
      details?: {
        durationMonths?: number;
        totalQuantity?: number;
        actualUnits?: number;
        spareUnits?: number;
        softwareChargeableUnits?: number;
        otherCostChargeableUnits?: number;
        hardwareCost?: number;
        softwareCost?: number;
        mobilizationCost?: number;
        basePrice?: number;
        discountPercentage?: number;
        modeMix?: {
          liveUnits?: number;
          offlineOnlyUnits?: number;
          weightedConnectedUnits?: number;
          liveModeMultiplier?: number;
          offlineModeMultiplier?: number;
        };
        otherCosts?: {
          internet?: number;
          cloud?: number;
          remoteSupport?: number;
          training?: number;
          provisioning?: number;
          onFieldSupport?: number;
        };
        spares?: Record<string, {
          actualUnits?: number;
          spareUnits?: number;
          sparePricingMode?: 'hw_only' | 'hw_sw' | 'hw_sw_other';
          softwareChargeableUnits?: number;
          otherCostChargeableUnits?: number;
        }>;
        totalOtherCosts?: number;
        totalPrice?: number;
      };
    }>;
    total?: number;
  };
}

const toNumber = (value: unknown): number | undefined => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  }
  return undefined;
};

const extractPayload = (result: unknown): PricingResponse => {
  if (typeof result === 'string') {
    try {
      return JSON.parse(result) as PricingResponse;
    } catch {
      return {};
    }
  }

  if (!result || typeof result !== 'object') {
    return {};
  }

  const obj = result as Record<string, unknown>;
  const body = obj.body;

  if (typeof body === 'string') {
    try {
      return JSON.parse(body) as PricingResponse;
    } catch {
      return {};
    }
  }

  if (body && typeof body === 'object') {
    return body as PricingResponse;
  }

  return obj as unknown as PricingResponse;
};

const extractProposalList = (result: unknown): ProposalRecord[] => {
  if (result && typeof result === 'object') {
    const obj = result as Record<string, unknown>;
    const body = obj.body;
    if (Array.isArray(body)) return body as ProposalRecord[];
    if (typeof body === 'string') {
      try { return JSON.parse(body) as ProposalRecord[]; } catch { return []; }
    }
  }
  if (Array.isArray(result)) return result as ProposalRecord[];
  return [];
};

const extractProposalRecord = (result: unknown): ProposalRecord => {
  if (result && typeof result === 'object') {
    const obj = result as Record<string, unknown>;
    const body = obj.body;
    if (body && typeof body === 'object' && !Array.isArray(body)) return body as ProposalRecord;
    if (typeof body === 'string') {
      try { return JSON.parse(body) as ProposalRecord; } catch { /* fall through */ }
    }
  }
  return result as ProposalRecord;
};

export const proposalService = {
  async calculatePrice(data: {
    selectedProducts: {
      [key: string]: SelectedProduct;
    };
    timeline: {
      startDate: string;
      endDate: string;
      duration: { value: string; unit: 'months' | 'years' | 'days' };
      milestones: Array<{
        id: string;
        title: string;
        description: string;
        targetDate: string;
      }>;
    };
    includePlatform?: boolean;
    numberOfUsers?: number;
    retentionTier?: 'cold' | 'warm' | null;
    retentionYears?: number;
    customerManagedCloud?: boolean;
    platformOverrides?: Record<string, number>;
    retentionOverrides?: Record<string, number>;
  }): Promise<{ totalPrice: number; breakdown?: PricingResponse['breakdown'] }> {
    const result = await apiPost<unknown>(
      '/proposals/calculate-price',
      data
    );
    const payload = extractPayload(result);
    const totalPrice =
      toNumber(payload.totalPrice) ??
      toNumber(payload.total_price) ??
      toNumber(payload.finalPrice);

    if (totalPrice === undefined) {
      throw new Error('Price response did not contain a valid total price');
    }

    return {
      totalPrice,
      breakdown: payload.breakdown
    };
  },

  async saveProposal(payload: SaveProposalPayload): Promise<ProposalRecord> {
    const body = {
      group_id: payload.groupId,
      ref_number: payload.refNumber,
      customer_name: payload.customerName,
      project_title: payload.projectTitle,
      total_price: payload.totalPrice,
      form_data: payload.formData,
      breakdown_data: payload.breakdownData,
      customer_pdf_base64: payload.customerPdfBase64,
      internal_pdf_base64: payload.internalPdfBase64,
    };
    const result = await apiPost<unknown>('/proposals', body);
    return extractProposalRecord(result);
  },

  async getProposals(): Promise<ProposalRecord[]> {
    const result = await apiGet<unknown>('/proposals');
    return extractProposalList(result);
  },

  async getProposalVersions(groupId: string): Promise<ProposalRecord[]> {
    const result = await apiGet<unknown>(`/proposals/${groupId}/versions`);
    return extractProposalList(result);
  },

  async overwriteProposal(id: string, payload: SaveProposalPayload): Promise<ProposalRecord> {
    const body = {
      group_id: payload.groupId,
      ref_number: payload.refNumber,
      customer_name: payload.customerName,
      project_title: payload.projectTitle,
      total_price: payload.totalPrice,
      form_data: payload.formData,
      breakdown_data: payload.breakdownData,
      customer_pdf_base64: payload.customerPdfBase64,
      internal_pdf_base64: payload.internalPdfBase64,
    };
    const result = await apiPut<unknown>(`/proposals/${id}`, body);
    return extractProposalRecord(result);
  },

  async deleteProposal(id: string): Promise<void> {
    await apiDelete(`/proposals/${id}`);
  },
};
